import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserTimesheet } from '@app/timesheet-user/models/user-timesheet.interface';
import { TimesheetStatus } from '@app/shared/constants/timesheet.constants';
import { ValidationStatus } from '@app/employees/components/periods-table/periods-table.constants';
import { ITimesheetGridError } from '../components/timesheet-table/timesheet-table.validator';

@Injectable({
  providedIn: 'root'
})
export class TimesheetDOS {
  previousSavedUserTimesheetData: UserTimesheet;
  updatedUserTimesheetData: UserTimesheet;
  changedTimesheetFieldsArr = [];

  private _timesheetDateSubject = new BehaviorSubject<Date>(new Date());
  readonly timesheetDate$ = this._timesheetDateSubject.asObservable();

  private _timesheetStatusSubject = new BehaviorSubject<TimesheetStatus>(null);
  readonly timesheetStatus$ = this._timesheetStatusSubject.asObservable();

  private _timesheetDataSubject = new BehaviorSubject<UserTimesheet>(null);
  readonly timesheetData$ = this._timesheetDataSubject.asObservable();

  private _isUserTimesheetChangedSubject = new BehaviorSubject<boolean>(false);
  readonly isUserTimesheetChanged$ = this._isUserTimesheetChangedSubject.asObservable();

  private _isUserEligibleSubject = new BehaviorSubject<boolean>(true);
  readonly isUserEligible$ = this._isUserEligibleSubject.asObservable();

  private _viewModeSubject = new BehaviorSubject<boolean>(true);
  readonly viewMode$ = this._viewModeSubject.asObservable();

  private _viewModeUserSubject = new BehaviorSubject<string>(null);
  readonly viewModeUser$ = this._viewModeUserSubject.asObservable();

  private _validationInProgressSubject = new BehaviorSubject<boolean>(null);
  readonly validationInProgress$ = this._validationInProgressSubject.asObservable();

  private _validationStatusSubject = new BehaviorSubject<ValidationStatus>(null);
  readonly validationStatus$ = this._validationStatusSubject.asObservable();

  private _validationErrorsSubject = new BehaviorSubject<ITimesheetGridError>(null);
  readonly validationErrors$ = this._validationErrorsSubject.asObservable();

  updateTimesheetDate(data: Date): void {
    this._timesheetDateSubject.next(data);
  }

  getTimesheetDateSnapshot(): Date {
    return this._timesheetDateSubject.value;
  }

  updateTimesheetData(data: UserTimesheet): void {
    this._timesheetDataSubject.next(data);
  }

  getTimesheetDataSnapshot(): UserTimesheet {
    return this._timesheetDataSubject.value;
  }

  updateTimesheetStatus(data: TimesheetStatus): void {
    this._timesheetStatusSubject.next(data);
  }

  getTimesheetStatusSnapshot(): TimesheetStatus {
    return this._timesheetStatusSubject.value;
  }

  getUserEligibilitySnapshot(): boolean {
    return this._isUserEligibleSubject.value;
  }

  updateUserEligibility(value: boolean): void {
    this._isUserEligibleSubject.next(value);
  }

  getUserTimesheetChangeStatusSnapshot(): boolean {
    return this._isUserTimesheetChangedSubject.value;
  }

  updateUserTimesheetChangedStatus(value: boolean): void {
    this._isUserTimesheetChangedSubject.next(value);
  }

  updateViewMode(data: boolean): void {
    this._viewModeSubject.next(data);
  }

  getViewModeUserSnapshot(): string {
    return this._viewModeUserSubject.value;
  }

  updateViewModeUser(data: string): void {
    this._viewModeUserSubject.next(data);
  }

  updateValidationInProgress(data: boolean): void {
    this._validationInProgressSubject.next(data);
  }

  getValidationErrorsSnapshot(): ITimesheetGridError {
    return this._validationErrorsSubject.value;
  }

  updateValidationErrors(data: ITimesheetGridError): void {
    this._validationErrorsSubject.next(data);
  }

  updateValidationStatus(data: ValidationStatus): void {
    this._validationStatusSubject.next(data);
  }
}
